import React from 'react';
import { usePermissions } from '@hooks/usePermissions';

// Props para o PermissionGuard
interface PermissionGuardProps {
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showError?: boolean;
}

// Props para o RoleGuard
interface RoleGuardProps {
  roles: string | string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean; // true = AND (todas as roles), false = OR (qualquer role)
}

/**
 * Componente para proteger elementos baseado em permissões específicas
 * Conforme especificado em SISTEMA_AUTORIZACAO.md
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  fallback = null,
  showError = false
}) => {
  const { checkPermission, isLoading, error } = usePermissions();

  // Aguardar carregamento da configuração
  if (isLoading) {
    return <div className="animate-pulse bg-slate-200 rounded h-4 w-16"></div>;
  }

  // Mostrar erro se configuração falhar
  if (error && showError) {
    console.error(`❌ PermissionGuard: Erro ao verificar permissão '${permission}':`, error);
    return (
      <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
        Erro ao carregar permissões: {error}
      </div>
    );
  }

  const result = checkPermission(permission);

  if (!result.hasPermission) {
    if (showError && result.reason) {
      return (
        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
          {result.reason}
        </div>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Componente para proteger elementos baseado em roles específicas
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  roles,
  children,
  fallback = null,
  requireAll = false
}) => {
  const { hasRole, isLoading } = usePermissions();

  // Aguardar carregamento
  if (isLoading) {
    return <div className="animate-pulse bg-slate-200 rounded h-4 w-16"></div>;
  }

  const roleList = Array.isArray(roles) ? roles : [roles];
  
  const hasAccess = requireAll
    ? roleList.every(role => hasRole(role))  // AND: todas as roles
    : roleList.some(role => hasRole(role));  // OR: qualquer role

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

